document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const applyThemeMode = (mode) => {
  const dark = mode === 'dark';
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
    button.setAttribute('aria-pressed', String(dark));
    button.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
    const label = button.querySelector('[data-theme-label]');
    if (label) label.textContent = dark ? 'Light Mode' : 'Dark Mode';
  });
};

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-theme-toggle]');
  if (!toggle) return;
  const next = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
  try { localStorage.setItem('iseebiz-theme', next); } catch (error) {}
  applyThemeMode(next);
});

applyThemeMode(document.documentElement.dataset.theme || 'light');

class ThemeSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;
    this.userPaused = false;
    this.interval = Number(this.dataset.interval || 5) * 1000;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelectorAll('[data-slide-dot]').forEach((dot) => dot.addEventListener('click', () => {
      this.show(Number(dot.dataset.slideDot));
      this.start();
    }));
    this.toggleButton = this.querySelector('[data-slider-toggle]');
    this.toggleButton?.addEventListener('click', () => this.setPaused(!this.userPaused));
    this.show(this.index, false);
    this.start();
  }
  show(next, announce = true) {
    if (!this.slides.length) return;
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => { slide.hidden = index !== this.index; slide.setAttribute('aria-hidden', String(index !== this.index)); });
    this.querySelectorAll('[data-slide-dot]').forEach((dot, index) => { if (index === this.index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
    this.resetProgress();
    const liveRegion = document.querySelector('[data-live-region]');
    if (announce && liveRegion) liveRegion.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
  }
  setPaused(paused) {
    this.userPaused = paused;
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-pressed', String(paused));
      this.toggleButton.setAttribute('aria-label', paused ? this.toggleButton.dataset.playLabel : this.toggleButton.dataset.pauseLabel);
    }
    if (paused) this.stop(); else this.start();
  }
  resetProgress() {
    this.classList.remove('is-running');
    void this.offsetWidth;
    if (this.autoplay && !this.userPaused && this.slides.length > 1) this.classList.add('is-running');
  }
  start() {
    this.stop();
    if (this.autoplay && !this.userPaused && this.slides.length > 1) {
      this.classList.add('is-running');
      this.timer = window.setInterval(() => this.show(this.index + 1, false), this.interval);
    }
  }
  stop() { window.clearInterval(this.timer); this.classList.remove('is-running'); }
}

class CountdownTimer extends HTMLElement {
  connectedCallback() { this.tick(); this.timer = window.setInterval(() => this.tick(), 1000); }
  disconnectedCallback() { window.clearInterval(this.timer); }
  tick() {
    const end = Date.parse(this.dataset.end);
    if (!end) return;
    const seconds = Math.max(0, Math.floor((end - Date.now()) / 1000));
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const values = [days, hours, minutes, secs].map((value) => String(value).padStart(2, '0'));
    const units = ['d', 'h', 'm', 's'];
    this.setAttribute('aria-label', `${days} days ${hours} hours ${minutes} minutes ${secs} seconds remaining`);
    this.innerHTML = `<svg class="announcement__clock" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="13" r="8.25" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 9v4l2.8 1.8M9 2h6M12 2v3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>${values.map((value, index) => `<span class="announcement__countdown-value">${value}</span><small class="announcement__countdown-unit">${units[index]}</small>${index < 3 ? '<span class="announcement__countdown-separator">:</span>' : ''}`).join('')}`;
    if (!seconds) window.clearInterval(this.timer);
  }
}

customElements.define('theme-slider', ThemeSlider);
customElements.define('countdown-timer', CountdownTimer);

class BannerSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.track = this.querySelector('[data-banner-track]');
    this.slides = [...this.querySelectorAll('[data-banner-slide]')];
    this.prev = this.querySelector('[data-banner-prev]');
    this.next = this.querySelector('[data-banner-next]');
    this.duration = Math.max(500, Number(this.dataset.duration || 1000));
    this.prev?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    this.track?.addEventListener('scroll', () => this.updateButtons(), { passive: true });
    this.updateButtons();
  }
  step() { const card = this.slides[0]; return card ? card.getBoundingClientRect().width + Number.parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth; }
  move(direction) { this.track?.scrollBy({ left: direction * this.step(), behavior: 'smooth' }); }
  updateButtons() { if (!this.track) return; const max = this.track.scrollWidth - this.track.clientWidth; if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2; if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2; }
  stop() { window.clearInterval(this.timer); }
  reveal(block) { if (!this.track || !block) return; this.stop(); this.track.scrollTo({ left: Math.max(0, block.offsetLeft - this.track.offsetLeft), behavior: 'smooth' }); }
}
if (!customElements.get('banner-slider')) customElements.define('banner-slider', BannerSlider);

const revealObserver = new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); } }), { rootMargin: '0px 0px -40px' });
document.querySelectorAll('[data-reveal]').forEach((element) => revealObserver.observe(element));

document.addEventListener('click', (event) => {
  const closeButton = event.target.closest('[data-close-details]');
  if (closeButton) closeButton.closest('details')?.removeAttribute('open');
});

document.querySelectorAll('[data-product-marker]').forEach((marker) => {
  marker.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) { marker.closest('.marker-section__media')?.querySelectorAll('[data-product-marker][open]').forEach((item) => { if (item !== marker) item.open = false; }); marker.open = true; } });
  marker.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) marker.open = false; });
});

document.addEventListener('shopify:block:select', (event) => {
  const slider = event.target.closest('theme-slider');
  if (slider) { slider.stop(); slider.show(slider.slides.indexOf(event.target), false); }
  event.target.closest('banner-slider')?.reveal(event.target);
});

document.addEventListener('shopify:block:deselect', (event) => {
  const slider = event.target.closest('theme-slider');
  if (slider?.autoplay && !slider.userPaused) slider.start();
});

document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-reveal]').forEach((element) => revealObserver.observe(element)));

const setFooterDisclosureState = () => {
  if (!window.matchMedia('(max-width: 599px)').matches) return;
  document.querySelectorAll('.footer-disclosure').forEach((disclosure) => disclosure.removeAttribute('open'));
};
setFooterDisclosureState();

class QuantityInput extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input');
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => { input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1); input.dispatchEvent(new Event('change', { bubbles: true })); });
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => { input.value = Number(input.value || 1) + 1; input.dispatchEvent(new Event('change', { bubbles: true })); });
  }
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.input?.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 250);
    });
  }
  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) { this.results.innerHTML = ''; return; }
    this.controller?.abort();
    this.controller = new AbortController();
    const url = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=6&section_id=predictive-search`;
    try {
      const response = await fetch(url, { signal: this.controller.signal });
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      this.results.innerHTML = documentFragment.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
    } catch (error) { if (error.name !== 'AbortError') this.results.innerHTML = ''; }
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    fetch(this.dataset.url).then((response) => response.text()).then((html) => {
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
      if (content?.innerHTML.trim()) this.innerHTML = content.innerHTML;
    }).catch(() => {});
  }
}

if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

async function refreshCartDrawer(open = true) {
  const response = await fetch('/?section_id=cart-drawer');
  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const nextDrawer = parsed.querySelector('dialog[data-cart-drawer]');
  const currentDrawer = document.querySelector('dialog[data-cart-drawer]');
  if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);
  if (open) document.querySelector('dialog[data-cart-drawer]')?.showModal();
}

async function refreshMainCart(section) {
  if (!section) return;
  const sectionId = section.dataset.sectionId;
  if (!sectionId) return;
  const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`);
  const html = await response.text();
  const nextSection = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-main-cart]');
  if (nextSection) section.replaceWith(nextSection);
}

async function updateCartLine(line, quantity, input) {
  const drawer = input?.closest('[data-cart-drawer]');
  drawer?.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    await refreshCartDrawer(true);
  } catch (error) {
    drawer?.removeAttribute('aria-busy');
    if (input) input.value = input.defaultValue;
  }
}

async function updateCartPageLine(line, quantity, source) {
  const section = source?.closest('[data-main-cart]');
  if (!section || section.getAttribute('aria-busy') === 'true') return;
  const sectionId = section.dataset.sectionId;
  section.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line,
        quantity,
        sections: [sectionId],
        sections_url: window.location.pathname
      })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    const cart = await response.json();
    const html = cart.sections?.[sectionId];
    if (!html) throw new Error('Unable to refresh cart');
    const nextSection = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-main-cart]');
    if (!nextSection) throw new Error('Unable to refresh cart');
    section.replaceWith(nextSection);
    document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
  } catch (error) {
    section.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', async (event) => {
  const cartPageRemove = event.target.closest('[data-cart-page-remove]');
  if (cartPageRemove) {
    event.preventDefault();
    await updateCartPageLine(Number(cartPageRemove.dataset.line), 0, cartPageRemove);
    return;
  }
  const removeItem = event.target.closest('[data-cart-remove]');
  if (removeItem) {
    event.preventDefault();
    await updateCartLine(Number(removeItem.dataset.line), 0, removeItem);
    return;
  }
  const quantityButton = event.target.closest('[data-cart-quantity-minus], [data-cart-quantity-plus]');
  if (quantityButton) {
    event.preventDefault();
    const control = quantityButton.closest('[data-cart-quantity]');
    const input = control?.querySelector('[data-cart-quantity-input]');
    if (control && input) {
      const delta = quantityButton.matches('[data-cart-quantity-plus]') ? 1 : -1;
      const quantity = Math.max(0, Math.floor(Number(input.value) || 0) + delta);
      input.value = quantity;
      await updateCartLine(Number(control.dataset.line), quantity, input);
    }
    return;
  }
  const cartOpen = event.target.closest('[data-cart-open]');
  if (cartOpen && document.querySelector('dialog[data-cart-drawer]')) { event.preventDefault(); document.querySelector('dialog[data-cart-drawer]').showModal(); }
  const mediaButton = event.target.closest('[data-media-target]');
  if (mediaButton) {
    const gallery = mediaButton.closest('[data-product-gallery]');
    gallery.querySelectorAll('[data-media-id]').forEach((media) => { media.hidden = media.dataset.mediaId !== mediaButton.dataset.mediaTarget; });
    gallery.querySelectorAll('[data-media-target]').forEach((button) => button.classList.toggle('is-active', button === mediaButton));
  }
  if (event.target.closest('[data-sticky-add]')) document.querySelector('[data-product-form]')?.requestSubmit();
  if (event.target.closest('[data-cart-close]')) event.target.closest('dialog[data-cart-drawer]')?.close();
  if (event.target.matches('dialog[data-cart-drawer], dialog[data-filter-drawer]')) event.target.close();
  if (event.target.closest('[data-open-filter]')) document.querySelector('[data-filter-drawer]')?.showModal();
  if (event.target.closest('[data-close-filter]')) event.target.closest('[data-filter-drawer]')?.close();
  const loadMore = event.target.closest('[data-load-more]');
  if (loadMore) {
    event.preventDefault();
    loadMore.setAttribute('aria-busy', 'true');
    const response = await fetch(loadMore.href);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    parsed.querySelectorAll('[data-product-grid] > *').forEach((card) => document.querySelector('[data-product-grid]')?.append(card));
    const next = parsed.querySelector('[data-load-more]');
    if (next) { loadMore.href = next.href; loadMore.removeAttribute('aria-busy'); } else loadMore.remove();
  }
});

document.addEventListener('change', (event) => {
  const cartPageQuantity = event.target.closest('[data-cart-page-quantity]');
  if (cartPageQuantity) {
    const quantity = Math.max(0, Math.floor(Number(cartPageQuantity.value) || 0));
    cartPageQuantity.value = quantity;
    updateCartPageLine(Number(cartPageQuantity.dataset.line), quantity, cartPageQuantity);
    return;
  }
  const cartQuantityInput = event.target.closest('[data-cart-quantity-input]');
  if (cartQuantityInput) {
    const control = cartQuantityInput.closest('[data-cart-quantity]');
    const quantity = Math.max(0, Math.floor(Number(cartQuantityInput.value) || 0));
    cartQuantityInput.value = quantity;
    updateCartLine(Number(control.dataset.line), quantity, cartQuantityInput);
    return;
  }
  const select = event.target.closest('[data-variant-select]');
  if (!select) return;
  const section = select.closest('[data-product-section]');
  const option = select.selectedOptions[0];
  section.querySelector('[data-price]').textContent = option.dataset.price;
  const compare = section.querySelector('[data-compare]');
  compare.textContent = option.dataset.compare;
  compare.hidden = !option.dataset.compare || option.dataset.compare === option.dataset.price;
  const sku = section.querySelector('[data-sku]');
  if (sku) { sku.hidden = !option.dataset.sku; sku.querySelector('span').textContent = option.dataset.sku; }
  const submit = section.querySelector('.product-form__submit');
  submit.disabled = option.dataset.available !== 'true';
  const addLabel = submit.querySelector('[data-add-label]');
  if (addLabel) addLabel.textContent = option.dataset.available === 'true' ? section.dataset.addText : section.dataset.soldText;
  const stickySubmit = section.querySelector('[data-sticky-add]');
  if (stickySubmit) { stickySubmit.disabled = option.dataset.available !== 'true'; stickySubmit.textContent = addLabel?.textContent || ''; }
  const stickyPrice = section.querySelector('[data-sticky-price]');
  if (stickyPrice) stickyPrice.textContent = option.dataset.price;
  if (option.dataset.mediaId) section.querySelector(`[data-media-target="${option.dataset.mediaId}"]`)?.click();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && event.target.matches('[data-cart-quantity-input], [data-cart-page-quantity]')) {
    event.preventDefault();
    event.target.blur();
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-product-form]');
  if (!form || form.dataset.cartDrawer !== 'true') return;
  event.preventDefault();
  const error = form.querySelector('[data-product-error]');
  const cartPage = form.closest('[data-main-cart]');
  try {
    const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
    if (!response.ok) throw new Error((await response.json()).description || 'Unable to add item');
    if (cartPage) await Promise.all([refreshCartDrawer(true), refreshMainCart(cartPage)]);
    else await refreshCartDrawer(true);
  } catch (requestError) { error.textContent = requestError.message; error.hidden = false; }
});

const enhanceShopifyPolicyMarkup = () => {
  const policyBody = document.querySelector('.shopify-policy__body .rte');
  const policyTitle = document.querySelector('.shopify-policy__title h1');
  if (!policyBody || !policyTitle) return;

  [...policyBody.children].forEach((element) => {
    if (element.tagName !== 'P') return;
    const text = element.textContent.trim();
    const markdownHeading = text.match(/^(#{1,3})\s+(.+)$/);

    if (markdownHeading) {
      const headingText = markdownHeading[2].trim();
      if (markdownHeading[1].length === 1 && headingText.toLowerCase() === policyTitle.textContent.trim().toLowerCase()) {
        element.remove();
        return;
      }
      const heading = document.createElement(markdownHeading[1].length === 1 ? 'h2' : 'h3');
      heading.textContent = headingText;
      element.replaceWith(heading);
      return;
    }

    const emphasizedLine = text.match(/^\*\*(.+)\*\*$/);
    if (emphasizedLine) {
      element.textContent = emphasizedLine[1].trim();
      element.classList.add('shopify-policy__updated');
    }
  });
};

enhanceShopifyPolicyMarkup();
